import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Mail, Globe2, Calendar, ArrowUpRight, TrendingUp, ShieldAlert, Award, CalendarDays, LayoutDashboard, PieChart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/leads/me");
        setLeads(data || []);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setLoadingLeads(false);
      }
    })();
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]">
        <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      {/* Premium Dashboard Header */}
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} aria-label="Zenstrust home" data-testid="brand-logo" className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              Member Portal
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/portfolio")}
              className="text-white hover:bg-white/10 hover:text-white font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex items-center"
            >
              <PieChart size={14} className="mr-1.5" /> Portfolio
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/profile")}
              className="text-white hover:bg-white/10 hover:text-white font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex items-center"
            >
              Profile
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/appointments")}
              className="text-white hover:bg-white/10 hover:text-white font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex items-center"
            >
              <CalendarDays size={14} className="mr-1.5" /> Appointments
            </Button>
            {(user.role === "admin" || user.role === "super_admin") && (
              <Button
                variant="ghost"
                onClick={() => navigate("/admin")}
                className="text-[hsl(var(--zt-gold))] hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-[hsl(var(--zt-gold))]/30 hidden sm:flex items-center"
              >
                <LayoutDashboard size={14} className="mr-1.5" /> Admin
              </Button>
            )}
            {user.role === "advisor" && (
              <Button
                variant="ghost"
                onClick={() => navigate("/advisor")}
                className="text-[hsl(var(--zt-gold))] hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-[hsl(var(--zt-gold))]/30 hidden sm:flex items-center"
              >
                <LayoutDashboard size={14} className="mr-1.5" /> Advisor Portal
              </Button>
            )}
            <Button
              variant="ghost"
              data-testid="dashboard-logout"
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-white hover:bg-white/10 hover:text-white font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10"
            >
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 lg:py-16 space-y-10">
        
        {/* Welcome Section */}
        <header className="space-y-4 max-w-4xl">
          <span className="font-heading uppercase tracking-[0.32em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
            {greeting()},
          </span>
          <h1 className="font-display text-4xl lg:text-6xl text-[hsl(var(--zt-teal))] tracking-tight leading-[1.05] font-bold">
            {user.name?.split(" ")[0] || "Investor"}
            <span className="text-[hsl(var(--zt-gold))] gold-text-glow font-display">.</span>
          </h1>
          <p className="text-base text-[hsl(var(--zt-charcoal))]/75 max-w-2xl font-body">
            Your customized NRI investment proposal is being prepared. Explore your dashboard, review your credentials, or connect with your strategic advisor below.
          </p>
        </header>

        {/* Wealth Chart / Portfolio allocation (Luxury Element) */}
        <section className="bg-white border border-[hsl(var(--zt-beige-deep))] rounded-3xl p-6 lg:p-8 shadow-[0_20px_50px_rgba(16,24,40,0.03)] grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[hsl(var(--zt-gold))]/10 border border-[hsl(var(--zt-gold))]/25 grid place-items-center">
                <TrendingUp size={16} className="text-[hsl(var(--zt-gold))]" />
              </div>
              <span className="font-heading uppercase tracking-[0.2em] text-[10px] text-[hsl(var(--zt-teal))]/75 font-bold">Zenstrust AI Projection</span>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-display text-3xl text-[hsl(var(--zt-teal))] font-semibold">Generational Wealth</h3>
              <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 leading-relaxed font-body">
                Our machine rebalancing engine projection models Indian equity, sovereign bonds, and prime commercial real estate access based on current global-local interest corridors.
              </p>
            </div>

            <div className="flex gap-8 pt-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 font-heading block mb-1">Target Allocation</span>
                <span className="text-lg font-bold text-[hsl(var(--zt-teal))] font-heading">AI Adaptive Equity</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 font-heading block mb-1">Expected Yield</span>
                <span className="text-lg font-bold text-[hsl(var(--zt-gold))] font-heading">~14.8% CAGR</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-[hsl(var(--zt-beige))]/40 border border-[hsl(var(--zt-beige-deep))]/50 rounded-2xl p-4 lg:p-6 flex flex-col items-center justify-center">
            {/* SVG line chart representing portfolio value */}
            <svg viewBox="0 0 500 200" className="w-full h-auto text-[hsl(var(--zt-teal))]" aria-hidden="true">
              <defs>
                <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4F63F0" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#4F63F0" stopOpacity="0.0" />
                </linearGradient>
              </defs>
              {/* Grid lines */}
              <line x1="10" y1="30" x2="490" y2="30" stroke="rgba(16,24,40,0.05)" strokeDasharray="3 3" />
              <line x1="10" y1="80" x2="490" y2="80" stroke="rgba(16,24,40,0.05)" strokeDasharray="3 3" />
              <line x1="10" y1="130" x2="490" y2="130" stroke="rgba(16,24,40,0.05)" strokeDasharray="3 3" />
              <line x1="10" y1="180" x2="490" y2="180" stroke="rgba(16,24,40,0.1)" />

              {/* Area */}
              <path
                d="M 10 180 Q 100 120 180 140 T 320 80 T 490 30 L 490 180 Z"
                fill="url(#chartGrad)"
              />

              {/* Line */}
              <path
                d="M 10 180 Q 100 120 180 140 T 320 80 T 490 30"
                fill="none"
                stroke="hsl(var(--zt-teal))"
                strokeWidth="2.5"
                strokeLinecap="round"
              />

              {/* Gold dotted line (Alternative model) */}
              <path
                d="M 10 180 C 130 160 250 140 490 100"
                fill="none"
                stroke="#4F63F0"
                strokeWidth="1.5"
                strokeDasharray="4 4"
              />

              {/* Indicators */}
              <circle cx="490" cy="30" r="4" fill="hsl(var(--zt-teal))" />
              <circle cx="490" cy="100" r="4" fill="#4F63F0" />
            </svg>
            <div className="flex justify-between w-full text-[10px] uppercase tracking-wider text-[hsl(var(--zt-muted))] mt-3 font-heading font-semibold">
              <span>Year 1</span>
              <span>Year 3</span>
              <span>Year 5 (Projected Strategy)</span>
            </div>
          </div>
        </section>

        {/* Dashboard Profile & CTA Widgets */}
        <div className="grid lg:grid-cols-12 gap-8">
          
          {/* Profile Card */}
          <article className="lg:col-span-5 bg-white border border-[hsl(var(--zt-beige-deep))] rounded-3xl p-8 space-y-6 shadow-[0_20px_50px_rgba(16,24,40,0.03)] flex flex-col justify-between">
            <div className="space-y-6">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/10 grid place-items-center">
                  <Award size={16} className="text-[hsl(var(--zt-teal))]" />
                </div>
                <h3 className="font-heading uppercase tracking-[0.22em] text-[10px] text-[hsl(var(--zt-teal))]/75 font-bold">Investor Profile</h3>
              </div>
              <div className="space-y-4 text-sm font-body border-t border-[hsl(var(--zt-teal))]/5 pt-4">
                <Row icon={Mail} label="Email" value={user.email} />
                <Row
                  icon={Globe2}
                  label="Sign-in"
                  value={
                    user.auth_provider === "google"
                      ? "Google Account"
                      : user.auth_provider === "both"
                      ? "Email + Google"
                      : "Email & password"
                  }
                />
                <Row
                  icon={Calendar}
                  label="Member since"
                  value={
                    user.created_at
                      ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })
                      : "—"
                  }
                />
              </div>
            </div>
            
            <div className="bg-[hsl(var(--zt-beige))]/50 border border-[hsl(var(--zt-beige-deep))]/50 rounded-2xl p-4 flex items-start gap-3 mt-4">
              <ShieldAlert size={16} className="text-[hsl(var(--zt-gold))] mt-0.5" />
              <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/70 leading-relaxed font-body">
                Ensure your Overseas Citizenship documents are verified to enable automatic DTAA tax harvest protocols.
              </p>
            </div>
          </article>

          {/* Luxury Next Step Widget */}
          <article className="lg:col-span-7 relative overflow-hidden bg-[hsl(var(--zt-teal))] text-white rounded-3xl p-8 lg:p-10 shadow-[0_30px_70px_-20px_rgba(16,24,40,0.35)] border border-white/5 flex flex-col justify-between">
            <div
              aria-hidden
              className="absolute -top-16 -right-16 w-72 h-72 rounded-full opacity-[0.12] blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }}
            />
            <div className="relative space-y-4">
              <span className="font-heading uppercase tracking-[0.32em] text-[10px] text-[hsl(var(--zt-gold))] font-bold">
                Next Step
              </span>
              <h3 className="font-display text-3xl lg:text-4xl mt-3 mb-4 max-w-md leading-tight font-bold">
                Schedule your first <em className="text-[hsl(var(--zt-gold))] not-italic font-display gold-text-glow">advisory call</em>.
              </h3>
              <p className="text-sm text-white/75 max-w-lg leading-relaxed font-body">
                Contextualize your tax residency requirements, timelines, and asset allocation targets—we'll align you with a dedicated advisor expert in cross-border wealth strategy.
              </p>
            </div>
            
            <div className="relative pt-6 mt-6 border-t border-white/10">
              <button
                onClick={() => navigate("/#lead-form")}
                data-testid="dashboard-cta"
                className="inline-flex items-center gap-2.5 bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 rounded-full px-8 h-12 font-heading uppercase tracking-[0.18em] text-xs font-semibold shadow-[0_4px_20px_rgba(79,99,240,0.25)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_8px_32px_rgba(79,99,240,0.35)]"
              >
                Request consultation <ArrowUpRight size={15} />
              </button>
            </div>
          </article>
        </div>

        {/* Lead/Conversation Requests list */}
        <section className="space-y-6 pt-6">
          <div className="flex items-center justify-between border-b border-[hsl(var(--zt-teal))]/10 pb-4">
            <h2 className="font-display text-2xl lg:text-3xl text-[hsl(var(--zt-teal))] font-bold">Your consultations</h2>
            <span className="font-heading uppercase tracking-[0.2em] text-[10px] text-[hsl(var(--zt-teal))]/60 font-bold bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/10 px-3 py-1 rounded-full">
              {leads.length} {leads.length === 1 ? "request" : "requests"}
            </span>
          </div>

          {loadingLeads ? (
            <div className="grid place-items-center py-12">
              <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" />
            </div>
          ) : leads.length === 0 ? (
            <div
              data-testid="leads-empty"
              className="bg-white border border-dashed border-[hsl(var(--zt-teal))]/20 rounded-3xl p-16 text-center space-y-4 shadow-[0_20px_50px_rgba(16,24,40,0.02)]"
            >
              <p className="font-heading text-[hsl(var(--zt-teal))] text-xl font-bold">No strategic requests registered</p>
              <p className="text-sm text-[hsl(var(--zt-charcoal))]/65 max-w-md mx-auto leading-relaxed font-body">
                Initiate your custom India portfolio strategy by lodging a consultation request from the home page.
              </p>
            </div>
          ) : (
            <div className="grid gap-6">
              {leads.map((l) => (
                <article
                  key={l.lead_id}
                  data-testid={`lead-row-${l.lead_id}`}
                  className="bg-white border border-[hsl(var(--zt-beige-deep))] rounded-3xl p-6 lg:p-8 grid lg:grid-cols-12 gap-6 items-center shadow-[0_20px_50px_rgba(16,24,40,0.02)] transition-all duration-300 hover:border-[hsl(var(--zt-gold))]/30 hover:shadow-[0_20px_60px_-20px_rgba(16,24,40,0.1)]"
                >
                  <div className="lg:col-span-3 space-y-1.5">
                    <p className="font-heading uppercase tracking-[0.2em] text-[9px] text-[hsl(var(--zt-teal))]/60 font-bold">Interest Area</p>
                    <p className="font-heading text-base text-[hsl(var(--zt-teal))] font-bold">{l.interest}</p>
                  </div>
                  <div className="lg:col-span-3 space-y-1.5">
                    <p className="font-heading uppercase tracking-[0.2em] text-[9px] text-[hsl(var(--zt-teal))]/60 font-bold">Residency</p>
                    <p className="text-sm text-[hsl(var(--zt-charcoal))]/85 font-body">{l.country}</p>
                  </div>
                  <div className="lg:col-span-4 space-y-1.5">
                    <p className="font-heading uppercase tracking-[0.2em] text-[9px] text-[hsl(var(--zt-teal))]/60 font-bold">Objectives Description</p>
                    <p className="text-sm text-[hsl(var(--zt-charcoal))]/75 line-clamp-2 font-body">{l.message || "—"}</p>
                  </div>
                  <div className="lg:col-span-2 lg:text-right space-y-2 lg:border-l lg:border-[hsl(var(--zt-teal))]/5 lg:pl-6">
                    <span className="inline-block font-heading uppercase tracking-[0.2em] text-[9px] text-[hsl(var(--zt-gold))] bg-[hsl(var(--zt-gold))]/10 border border-[hsl(var(--zt-gold))]/20 px-3 py-1 rounded-full font-bold">
                      {l.status || "new"}
                    </span>
                    <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/60 font-heading font-semibold">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-[hsl(var(--zt-gold))] flex-shrink-0" />
      <span className="font-heading uppercase tracking-[0.2em] text-[10px] text-[hsl(var(--zt-teal))]/65 w-28 font-bold">{label}</span>
      <span className="text-[hsl(var(--zt-charcoal))]/85 font-semibold text-sm">{value}</span>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
